import React from 'react';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/core/utils';
import { EmployeePlanFulfillment } from '@/hooks/employees/usePlanFulfillment';
import { TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';

interface EmployeePlanProgressProps {
  fulfillment: EmployeePlanFulfillment | undefined;
  compact?: boolean;
  showTooltip?: boolean;
}

const statusConfig = {
  ahead: {
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-500',
    progressColor: 'bg-green-500',
    icon: TrendingUp,
    label: 'Ahead of plan',
  },
  on_track: {
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-500',
    progressColor: 'bg-emerald-500',
    icon: Minus,
    label: 'On track',
  },
  behind: {
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-500',
    progressColor: 'bg-amber-500',
    icon: TrendingDown,
    label: 'Behind plan',
  },
  significantly_behind: {
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-500',
    progressColor: 'bg-red-500',
    icon: AlertCircle,
    label: 'Significantly behind',
  },
  no_target: {
    color: 'text-muted-foreground',
    bgColor: 'bg-muted',
    progressColor: 'bg-muted',
    icon: Minus,
    label: 'No target set',
  },
};

/**
 * Compact inline plan fulfillment indicator for employee table.
 * Shows actual hours worked vs planned hours vs target with progress indicators.
 */
export const EmployeePlanProgress: React.FC<EmployeePlanProgressProps> = ({
  fulfillment,
  compact = false,
  showTooltip = true,
}) => {
  if (!fulfillment) {
    return (
      <span className="text-xs text-muted-foreground">-</span>
    );
  }

  const { hoursWorked, hoursPlanned, hoursTarget, actualProgressPercent, plannedProgressPercent, status, expectedProgressPercent } = fulfillment;
  const config = statusConfig[status];
  const StatusIcon = config.icon;

  const content = (
    <div className={cn('flex items-center gap-2', compact ? 'min-w-[100px]' : 'min-w-[180px]')}>
      {/* Hours display: Actual / Planned / Target */}
      <div className="flex items-center gap-1">
        <span className={cn('font-medium tabular-nums', config.color, compact ? 'text-xs' : 'text-sm')}>
          {hoursWorked}
        </span>
        <span className={cn('text-muted-foreground', compact ? 'text-xs' : 'text-sm')}>
          /
        </span>
        <span className={cn('tabular-nums text-blue-600 dark:text-blue-400', compact ? 'text-xs' : 'text-sm')}>
          {hoursPlanned}
        </span>
        <span className={cn('text-muted-foreground', compact ? 'text-xs' : 'text-sm')}>
          /{hoursTarget}h
        </span>
      </div>

      {/* Status icon */}
      {status !== 'no_target' && (
        <StatusIcon className={cn('h-3.5 w-3.5 flex-shrink-0', config.color)} />
      )}

      {/* Dual progress bars */}
      {!compact && status !== 'no_target' && (
        <div className="flex-1 min-w-[50px] max-w-[70px] space-y-0.5">
          {/* Actual progress (from time tracking) */}
          <Progress
            value={Math.min(actualProgressPercent, 100)}
            className="h-1"
            indicatorClassName={config.progressColor}
          />
          {/* Planned progress (from shifts) */}
          <Progress
            value={Math.min(plannedProgressPercent, 100)}
            className="h-1"
            indicatorClassName="bg-blue-500"
          />
        </div>
      )}
    </div>
  );

  if (!showTooltip || status === 'no_target') {
    return content;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">{content}</div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[240px]">
          <div className="space-y-2">
            <div className="font-medium">{config.label}</div>
            <div className="text-xs space-y-1">
              <div className="flex justify-between">
                <span className={config.color}>Actual (tracked):</span>
                <span className="font-medium">{hoursWorked}h ({actualProgressPercent}%)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-600 dark:text-blue-400">Planned (shifts):</span>
                <span className="font-medium">{hoursPlanned}h ({plannedProgressPercent}%)</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Target:</span>
                <span>{hoursTarget}h</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Expected by now:</span>
                <span>{expectedProgressPercent}%</span>
              </div>
            </div>
            {fulfillment.shiftsScheduled > 0 && (
              <div className="text-xs text-muted-foreground pt-1 border-t">
                {fulfillment.shiftsCompleted}/{fulfillment.shiftsScheduled} shifts completed
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Badge variant for more prominent display (e.g., in cards or headers)
 */
export const EmployeePlanProgressBadge: React.FC<EmployeePlanProgressProps> = ({
  fulfillment,
}) => {
  if (!fulfillment || fulfillment.status === 'no_target') {
    return (
      <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-muted text-muted-foreground text-xs">
        <Minus className="h-3 w-3" />
        <span>No target</span>
      </div>
    );
  }

  const { hoursWorked, hoursTarget, progressPercent, status } = fulfillment;
  const config = statusConfig[status];
  const StatusIcon = config.icon;

  return (
    <div className={cn(
      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
      status === 'ahead' && 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      status === 'on_track' && 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      status === 'behind' && 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      status === 'significantly_behind' && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    )}>
      <StatusIcon className="h-3 w-3" />
      <span>{hoursWorked}/{hoursTarget}h</span>
      <span className="text-[10px] opacity-75">({progressPercent}%)</span>
    </div>
  );
};

export default EmployeePlanProgress;
